import { useHistory, useLocation } from 'react-router-dom'

import { Languages } from './translations/config'
import { useSelectedLanguage } from './translations/utils'
import Home from './pages/Home'
import Status from './pages/Status'
import { useCallback } from 'react'
import AdditionalInformation from './pages/AdditionalInformation'
import Error404 from './pages/errors/404'

export type SharedRouteParams = {
  lang: Languages
}

export type RouteParams = {[key: string]: string} & SharedRouteParams

// Update as new pages are added
type PageName = 'home' | 'status' | 'additional-information' | 'error-404'

type RouteConfig = {
  name: PageName,
  path: string
  Component: React.ComponentType<any>
  exact?: boolean,
  params: {[key: string]: string}
}

export const langParamName = 'lang'

export const routeConfig: RouteConfig[] = [
  {
    name: 'status',
    path: '/status',
    Component: Status,
    exact: true,
    params: {}
  },
  {
    name: 'additional-information',
    path: '/additional-information',
    Component: AdditionalInformation,
    exact: true,
    params: {}
  },
  {
    name: 'home',
    path: '/',
    Component: Home,
    exact: true,
    params: {}
  },
  {
    name: 'error-404',
    path: '*',
    Component: Error404,
    exact: true,
    params: {}
  }
]

export const findRouteByPath = (path: string): RouteConfig | undefined => {
  return routeConfig.find(route => route.path === path)
}

export const buildRoutePathWithLang = (path: string) => {
  return path === '/' || path === '' ? `/:${langParamName}` : `/:${langParamName}${path}`
}

export const buildLinkForPath = (path: string, localeCode: Languages) => {
  return path === '/' || path === '' ? `/${localeCode}` : `/${localeCode}${path}`
}

export const usePathname = (): string => {
  const { pathname } = useLocation()
  return pathname.replace(/^\/([A-z]|-)+(\/)?/, '$2')
}

export const useGoToPath = (): (path: string) => void => {
  const lang = useSelectedLanguage()
  const history = useHistory()

  const goToPath = useCallback((path: string) => {
    history.push(buildLinkForPath(path, lang))
  }, [history, lang])

  return goToPath
}

export const useGoToPage = (): (page: PageName) => void => {
  const goToPath = useGoToPath()

  const goToPage = useCallback((pageName: PageName) => {
    const route = routeConfig.find(route => route.name === pageName)

    if (route) {
      goToPath(route.path)
    } else {
      throw new Error(`Page ${pageName} not found`)
    }
  }, [goToPath])

  return goToPage
}
